/**
 * @fileoverview Component for displaying and managing a participant's profile information.
 * This component includes personal details, external IDs, study information, and language preferences.
 * It provides functionality to add/remove external IDs and update study information.
 *
 * @example
 * // Render the profile tab for a participant
 * <ProfileTab />
 *
 * @example
 * // Format a date string
 * formatDate("2023-01-01T12:00:00Z") // Returns "Jan 01, 2023, 12:00PM"
 *
 * @example
 * // Format language and proficiency
 * formatLanguage("English", "Native") // Returns <span>English, Native</span>
 */


import { LinkIcon } from '@heroicons/react/20/solid'
import { PlusIcon } from '@heroicons/react/24/outline'
import { parsePhoneNumber } from 'libphonenumber-js'
import { DateTime } from 'luxon'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import CopyIcon from '../../../assets/copy.svg'
import GBFlagIcon from '../../../assets/flags/gb.svg'
import USFlagIcon from '../../../assets/flags/us.svg'
import StudyIcon from '../../../assets/menu/studies.svg'
import ScoreIcon from '../../../assets/score.svg'
import SiteIcon from '../../../assets/site.svg'
import TrashIcon from '../../../assets/trash.svg'
import { States } from '../../../config'
import { useModal } from '../../../hooks/useModal'
import { useToast } from '../../../hooks/useToast'
import {
    useAddExternalIdMutation,
    useGetParticipantByIdQuery,
    useRemoveExternalIdMutation,
    useUpdateParticipantStudyIdMutation,
} from '../../../services/participants'
import { useGetStudiesQuery } from '../../../services/studies'
import { formatOutput } from '../../../utils/formatOutput'
import { formatScore as formatScoreLabel } from '../../../utils/formatScore'
import { ProfileBlock } from '../../ProfileBlock'
import StudyDropdown from '../../StudyDropdown'
import { AddNewExternalID } from '../../modals/content/AddNewExternalID'
import { ConfirmDeleteExternalID } from '../../modals/content/ConfirmDeleteExternalID'
import { StudyVerificationModal } from '../../modals/content/StudyVerification'

/**
 * @function formatDate
 * @description Formats a date string into a readable format.
 * @param {string} date - The date string to format.
 * @returns {string} - The formatted date string.
 */
function formatDate(date: string) {
    const d = DateTime.fromISO(date)
    const formattedDate = d.toFormat('LLL dd, yyyy, h:mma')
    return formatOutput(formattedDate)
}

/**
 * @function formatLanguage
 * @description Formats language and proficiency into a readable format.
 * @param {string} language - The language to format.
 * @param {string} proficiency - The proficiency to format.
 * @returns {JSX.Element} - The formatted language and proficiency.
 */
function formatLanguage(language?: string, proficiency?: string) {
    if (!language) {
        return formatOutput(language)
    }

    return (
        <span>
            {formatOutput(language)}, {formatOutput(proficiency)}
        </span>
    )
}

/**
 * @function formatScore
 * @description Formats a score and level into a readable format.
 * @param {number} score - The score to format.
 * @param {string} level - The level to format.
 * @returns {JSX.Element} - The formatted score and level.
 */
function formatScore(score?: number, level?: string) {
    if (score === undefined) return formatOutput(score)

    return (
        <div className="flex items-center">
            <ScoreIcon className="mr-2" />
            {formatScoreLabel(score, level)}
        </div>
    )
}

/**
 * @function formatID
 * @description Formats an ID with a prefix.
 * @param {string} prefix - The prefix to format.
 * @param {string} id - The ID to format.
 * @returns {JSX.Element} - The formatted ID.
 */
function formatID(prefix: string, id: string) {
    return formatOutput(prefix + '–' + id.split('-')[0])
}

/**
 * @function calculateAge
 * @description Calculates the age from a birth date.
 * @param {string} date - The birth date to calculate.
 * @returns {number} - The calculated age.
 */
function calculateAge(date: string): number {
    const ageDifMs = Date.now() - new Date(date).getTime()
    const ageDate = new Date(ageDifMs)

    return Math.abs(ageDate.getUTCFullYear() - 1970)
}

/**
 * @function getCountryIcon
 * @description Returns the country icon based on the country code.
 * @param {string} countryCode - The country code to get the icon for.
 * @returns {JSX.Element | null} - The country icon or null if the country code is not supported.
 */
function getCountryIcon(countryCode: string) {
    switch (countryCode) {
        case 'US':
            return <USFlagIcon className="w-6 h-6" />
        case 'GB':
            return <GBFlagIcon className="w-6 h-6" />
        default:
            return null
    }
}

/**
 * @function formatSite
 * @description Formats the site location and distance.
 * @param {string} location - The location to format.
 * @param {number} distance - The distance to format.
 * @returns {JSX.Element} - The formatted site location and distance.
 */
function formatSite(location: string, distance?: number) {
    if (location) {
        return (
            <div className="flex gap-x-2">
                <SiteIcon className="text-indigo-500" />
                <div>
                    <span className="font-medium">{location}</span>
                    {distance != undefined && (
                        <span className="font-normal text-gray-600">
                            {' '}
                            {Math.round(Math.round(distance) / 100) / 10} km away
                        </span>
                    )}
                </div>
            </div>
        )
    }

    return <span className="text-gray-400">n/a</span>
}

/**
 * @function ProfileTab
 * @description Component for displaying and managing a participant's profile information.
 * @returns {JSX.Element | null} - The ProfileTab component.
 */
export function ProfileTab() {
    const { participantId } = useParams()
    const { t } = useTranslation()
    const [open] = useModal()
    const { data: participant, isLoading } = useGetParticipantByIdQuery(participantId)
    const [addExternalID] = useAddExternalIdMutation()
    const [removeExternalID] = useRemoveExternalIdMutation()
    const [updateStudyId] = useUpdateParticipantStudyIdMutation()
    const { data: studies, isLoading: areStudiesLoading } = useGetStudiesQuery()
    const [openToast] = useToast()

    if (isLoading || areStudiesLoading) {
        return null
    }

    function onCopyLink(link: string | undefined) {
        navigator.clipboard.writeText(link)
        openToast({
            title: 'Link copied!',
            icon: <LinkIcon className="h-6 w-6 text-indigo-400" aria-hidden="true" />,
        })
    }

    function formatPhoneNumber(phoneNumber: string | undefined) {
        if (!phoneNumber) return formatOutput(undefined)

        const number = parsePhoneNumber(phoneNumber)
        const country = number?.country
        return (
            <div className="flex group whitespace-nowrap w-fit gap-x-1 -ml-2 px-2 py-1 transition-all rounded hover:text-indigo-700 cursor-pointer hover:bg-indigo-100">
                <button
                    className="flex gap-x-2 items-center"
                    onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        onCopyLink(number.format('E.164'))
                    }}
                >
                    {getCountryIcon(country as string)}
                    {formatOutput(number.formatInternational())}
                    <CopyIcon className="h-5 group-hover:fill-indigo-700 hover:fill-indigo-700" />
                </button>
            </div>
        )
    }

    function formatEmail(email: string | undefined) {
        if (!email) return formatOutput(undefined)

        return (
            <div className="flex group whitespace-nowrap w-fit gap-x-1 -ml-2 px-2 py-1 transition-all rounded hover:text-indigo-700 cursor-pointer hover:bg-indigo-100">
                <button
                    className="flex gap-x-2 items-center"
                    onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        onCopyLink(email)
                    }}
                >
                    {formatOutput(email)}
                    <CopyIcon className="h-5 group-hover:fill-indigo-700 hover:fill-indigo-700" />
                </button>
            </div>
        )
    }

    const isCompleted = participant.assignment?.status === 'complete'

    function formatExternalIDs(externals?: Novoic.API.ExternalID[]): [string, JSX.Element][] {
        return (
            externals?.map((external) => [
                external.name || 'External ID',
                <button
                    key={external.id}
                    className="flex gap-x-1 -ml-2 px-2 py-1 rounded group hover:bg-red-50"
                    onClick={() => {
                        open({
                            content: (
                                <ConfirmDeleteExternalID
                                    onConfirm={async () =>
                                        removeExternalID({
                                            participantId: external.patient_id,
                                            externalId: external.id,
                                        })
                                    }
                                />
                            ),
                        })
                    }}
                >
                    <span className="group-hover:text-red-700">{external.external_id}</span>
                    <TrashIcon className="hidden group-hover:block group-hover:fill-red-700 cursor-pointer" />
                </button>,
            ]) || []
        )
    }

    async function onChangeStudy(newStudyId: string) {
        async function onSubmit(
            from: States,
            to: States,
            annotation: string,
            status: States,
            credentials?: { username: string; password: string }
        ) {
            updateStudyId({
                participantId: participantId,
                studyId: participant.studies[0]?.study.id,
                targetStudyId: newStudyId,
                status,
                credentials,
            })
        }

        const newStudy = studies.find((item) => item.id === newStudyId)

        const from = {
            name: participant.studies[0]?.study.name,
            value: participant.studies[0]?.study.id,
            listIcon: <StudyIcon className="fill-indigo-500 stroke-gray-50" />,
        }

        const to = {
            name: newStudy?.name,
            value: newStudy?.id,
            listIcon: <StudyIcon className="fill-indigo-500 stroke-gray-50" />,
        }

        open({
            content: (
                <StudyVerificationModal
                    onSubmit={onSubmit}
                    status={participant.studies[0]?.status as unknown as States}
                    transition={{ from, to }}
                />
            ),
        })
    }

    return (
        <div className="p-8">
            <ProfileBlock
                title="Details"
                content={[
                    [
                        'Score',
                        isCompleted
                            ? formatScore(
                                  participant.assignment?.score,
                                  participant.assignment?.level
                              )
                            : formatOutput(undefined),
                    ],
                    [
                        'Study',
                        <StudyDropdown
                            key="study-dropdown"
                            currentStudyId={participant.studies[0]?.study?.id}
                            onChange={onChangeStudy}
                        />,
                    ],
                    [
                        'Site',
                        formatSite(
                            participant.sites?.[0]?.site.name,
                            participant.assignment?.distance
                        ),
                    ],
                    ['Created by', formatOutput(participant.user?.name)],
                    ['Creation date', formatDate(participant.created_at)],
                    ['Participant ID', formatID('PID', participant.id)],
                    ...formatExternalIDs(participant.externals),
                    !participant.externals?.length && [
                        'External ID',
                        <button
                            key="add-external-id"
                            className="flex gap-x-1 -ml-2 px-2 py-1 rounded text-indigo-600 hover:text-indigo-700 cursor-pointer hover:bg-indigo-100"
                            onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                open({
                                    content: (
                                        <AddNewExternalID
                                            onConfirm={async (form) => {
                                                await addExternalID({
                                                    participantId: participant.id,
                                                    name: form.name,
                                                    externalId: form.external_id,
                                                })
                                            }}
                                        />
                                    ),
                                })
                            }}
                        >
                            <PlusIcon className="h-5 stroke-indigo-600" />
                            External ID
                        </button>,
                    ],
                ]}
            />
            {participant.externals?.length && (
                <button
                    className="flex mt-1 gap-x-1 -ml-2 px-2 py-1 rounded text-indigo-600 hover:text-indigo-700 cursor-pointer hover:bg-indigo-100"
                    onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        open({
                            content: (
                                <AddNewExternalID
                                    onConfirm={async (form) => {
                                        await addExternalID({
                                            participantId: participant.id,
                                            name: form.name,
                                            externalId: form.external_id,
                                        })
                                    }}
                                />
                            ),
                        })
                    }}
                >
                    <PlusIcon className="h-5 stroke-indigo-600" />
                    <span className="text-sm font-medium">Add External ID</span>
                </button>
            )}

            <div className="mt-8">
                <ProfileBlock
                    title="Demographics"
                    content={[
                        ['Name', formatOutput(participant.name)],
                        [
                            'Language',
                            formatLanguage(
                                participant.language &&
                                    t(`demographics.language.${participant.language}`),
                                participant.language_proficiency &&
                                    t(
                                        `demographics.proficiency.${participant.language_proficiency}`
                                    )
                            ),
                        ],
                        [
                            'Age',
                            participant.birth_date
                                ? formatOutput(
                                      t('demographics.age', {
                                          age: calculateAge(participant.birth_date),
                                      })
                                  )
                                : formatOutput(undefined),
                        ],
                        [
                            'Biological sex',
                            formatOutput(
                                participant.gender && t(`demographics.gender.${participant.gender}`)
                            ),
                        ],
                        [
                            'Education level',
                            formatOutput(
                                participant.education_level &&
                                    t(`demographics.education.${participant.education_level}`)
                            ),
                        ],
                        [
                            'Ethnicity',
                            formatOutput(
                                participant.ethnicity &&
                                    t(`demographics.hispanic.${participant.ethnicity}`)
                            ),
                        ],
                        [
                            'Racial background',
                            participant.races?.length > 0
                                ? formatOutput(
                                      participant.races
                                          .map((race) =>
                                              t(`demographics.races.${race.replace(/"/g, '')}`)
                                          )
                                          .join(', ')
                                  )
                                : formatOutput(undefined),
                        ],
                    ]}
                />
            </div>

            <div className="mt-8">
                <ProfileBlock
                    title="Contacts"
                    content={[
                        ['Phone number', formatPhoneNumber(participant.phone)],
                        ['Email', formatEmail(participant.email)],
                        ['Location', formatOutput(participant.location)],
                    ]}
                />
            </div>
        </div>
    )
}
